import { moveItemInArray } from '@angular/cdk/drag-drop';
import { inject, Injectable } from '@angular/core';
import {
  BasicDialogService,
  DialogPresets,
} from '@principle-theorem/ng-shared';
import { CustomReport } from '@principle-theorem/principle-core';
import {
  CustomChartType,
  ICustomChartSettings,
  ICustomReport,
  ICustomReportChart,
  ICustomReportChartSection,
  ICustomReportColumn,
  ICustomReportFilter,
  isCustomReportColumn,
  MeasureFormatter,
  MeasureReducer,
} from '@principle-theorem/principle-core/interfaces';
import {
  ReportBuilderDataSource,
  ReportBuilderDataSourceInstance,
} from '@principle-theorem/reporting';
import { uid, WithRef } from '@principle-theorem/shared';
import { type Crossfilter } from 'crossfilter2';
import { first, omit } from 'lodash';
import {
  CustomReportDialogComponent,
  ICustomReportDialogRequest,
  ICustomReportDialogResponse,
} from '../custom-reports/custom-report/custom-report-dialog/custom-report-dialog.component';
import {
  EditReportFilterDialogComponent,
  IEditReportFilterDialogRequest,
  IEditReportFilterDialogResponse,
} from '../custom-reports/custom-report/edit-report-filter-dialog/edit-report-filter-dialog.component';
import {
  IReportBuilderAddChartRequest,
  IReportBuilderAddChartResponse,
  ReportBuilderAddChartDialogComponent,
} from './report-builder-results-table/report-builder-add-chart-dialog/report-builder-add-chart-dialog.component';
import {
  IReportBuildColumnSelectorFormData,
  IReportBuilderColumnSelectorDialogData,
  ReportBuilderColumnSelectorDialogComponent,
} from './report-builder-results-table/report-builder-column-selector-dialog/report-builder-column-selector-dialog.component';
import {
  IReportBuilderTableNameDialogData,
  IReportBuildTableNameFormData,
  ReportBuilderTableNameDialogComponent,
} from './report-builder-results-table/report-builder-table-name-dialog/report-builder-table-name-dialog.component';

@Injectable()
export class ReportEditorService {
  private _dialog = inject(BasicDialogService);

  async openCustomReportDialog(
    request: ICustomReportDialogRequest = {}
  ): Promise<ICustomReportDialogResponse | undefined> {
    return this._dialog
      .open<
        CustomReportDialogComponent,
        ICustomReportDialogRequest,
        ICustomReportDialogResponse
      >(CustomReportDialogComponent, DialogPresets.medium({ data: request }))
      .afterClosed()
      .toPromise();
  }

  deleteChart(
    chartSections: ICustomReportChartSection[],
    chart: ICustomReportChart
  ): ICustomReportChartSection[] {
    return chartSections.map((section) => ({
      ...section,
      charts: section.charts.filter((item) => item.uid !== chart.uid),
    }));
  }

  async editFilter(
    dataSource: ReportBuilderDataSourceInstance,
    filter?: ICustomReportFilter
  ): Promise<IEditReportFilterDialogResponse | undefined> {
    const data: IEditReportFilterDialogRequest = {
      dataSource,
      filter,
    };

    const response = await this._dialog
      .open<
        EditReportFilterDialogComponent,
        IEditReportFilterDialogRequest,
        IEditReportFilterDialogResponse
      >(
        EditReportFilterDialogComponent,
        DialogPresets.medium({ data, autoFocus: filter ? false : true })
      )
      .afterClosed()
      .toPromise();

    if (!response) {
      return;
    }
    return response;
  }

  async editChart(
    dataSource: ReportBuilderDataSourceInstance,
    chart?: ICustomReportChart,
    filteredData: object[] = [],
    ndx?: Crossfilter<object>
  ): Promise<ICustomReportChart | undefined> {
    const data: IReportBuilderAddChartRequest = {
      dataSource,
      measures: [
        ...ReportBuilderDataSource.allColumns(dataSource).map(
          (column) => column.measure
        ),
        dataSource.factTable.count,
      ],
      groupBys: dataSource?.groupByOptions ?? [],
      filteredData,
      chart,
      ndx,
    };

    const updates = await this._dialog
      .open<
        ReportBuilderAddChartDialogComponent,
        IReportBuilderAddChartRequest,
        IReportBuilderAddChartResponse
      >(
        ReportBuilderAddChartDialogComponent,
        DialogPresets.almostFullscreen({ data })
      )
      .afterClosed()
      .toPromise();

    if (!updates) {
      return;
    }

    const settings =
      updates.type === CustomChartType.Table ? chart?.settings : undefined;
    return omit(
      {
        uid: uid(true),
        ...chart,
        type: updates.type,
        label: updates.label,
        groupBy: updates.groupBy.metadata.id,
        settings: settings ?? [
          {
            uid: uid(true),
            label: updates.measure.metadata.label,
            dataPoint: updates.measure.metadata.id,
            reducer: updates.reducer,
            filters: updates.filters,
          },
        ],
        builderConfig: {
          rotateXAxisLabels:
            updates.type === CustomChartType.Column ? true : false,
        },
        builderData: {
          colourOverride: !!updates.groupBy.groupMeasure.colourAccessor,
          plottedOverTime: [
            MeasureFormatter.Timestamp,
            MeasureFormatter.Day,
          ].includes(
            updates.groupBy.metadata.formatter ?? MeasureFormatter.Text
          )
            ? true
            : false,
        },
      },
      ['isPlaceholder', 'placeholderMessage']
    );
  }

  async editTable(
    chart: ICustomReportChart
  ): Promise<ICustomReportChart | undefined> {
    const data: IReportBuilderTableNameDialogData = { chart };

    const updates = await this._dialog
      .open<
        ReportBuilderTableNameDialogComponent,
        IReportBuilderTableNameDialogData,
        IReportBuildTableNameFormData
      >(ReportBuilderTableNameDialogComponent, DialogPresets.small({ data }))
      .afterClosed()
      .toPromise();

    if (!updates) {
      return;
    }

    return {
      ...chart,
      label: updates.label,
    };
  }

  moveChart(
    customReport: WithRef<ICustomReport>,
    indexOffset: number,
    chart: ICustomReportChart
  ): ICustomReportChartSection[] {
    return customReport.chartSections.map((section) => {
      const sectionCharts = [...section.charts];
      const currentIndex = sectionCharts.findIndex(
        (item) => item.uid === chart.uid
      );
      if (currentIndex === -1) {
        return section;
      }
      moveItemInArray(sectionCharts, currentIndex, currentIndex + indexOffset);
      return { ...section, charts: sectionCharts };
    });
  }

  upsertChart(
    chartSections: ICustomReportChartSection[],
    chart: ICustomReportChart,
    upsertIndex?: number
  ): ICustomReportChartSection[] {
    let updatedCount = 0;
    const updatedSections = chartSections.map((section) => {
      const charts = [...section.charts];
      const chartIndex: number = charts.findIndex(
        (existingChart) => existingChart.uid === chart.uid
      );
      if (chartIndex >= 0) {
        charts.splice(chartIndex, 1, chart);
        updatedCount += 1;
      }
      return { ...section, charts };
    });
    if (updatedCount) {
      return updatedSections;
    }

    const existingSection = first(chartSections);
    if (existingSection) {
      if (upsertIndex && upsertIndex >= 0) {
        existingSection.charts.splice(upsertIndex, 0, chart);
      } else {
        existingSection.charts.push(chart);
      }
      return chartSections;
    }

    return [...chartSections, CustomReport.chartSection('Charts', [chart])];
  }

  async openEditColumnDialog(
    existing?: ICustomReportColumn | ICustomChartSettings,
    filteredData: object[] = [],
    excludeDataPointFormatters: MeasureFormatter[] = []
  ): Promise<IReportBuildColumnSelectorFormData | undefined> {
    const data: IReportBuilderColumnSelectorDialogData = {
      existing: isCustomReportColumn(existing)
        ? this._columnToChartSettings(existing)
        : existing,
      filteredData,
      excludeDataPointFormatters,
    };
    return this._dialog
      .open<
        ReportBuilderColumnSelectorDialogComponent,
        IReportBuilderColumnSelectorDialogData,
        IReportBuildColumnSelectorFormData
      >(
        ReportBuilderColumnSelectorDialogComponent,
        DialogPresets.medium({ data })
      )
      .afterClosed()
      .toPromise();
  }

  private _columnToChartSettings(
    existing: ICustomReportColumn
  ): ICustomChartSettings {
    return {
      uid: existing.uid,
      label: existing.label ?? '',
      dataPoint: existing.id,
      reducer: MeasureReducer.Sum,
    };
  }
}
